import { inject, InjectionToken, Service } from '@angular/core';
import mixpanel, { type Dict } from 'mixpanel-browser';
import { environment } from '@environments/environment';

export interface IAnalyticsConfig {
  enabled: boolean;
  token: string;
}

export const ANALYTICS_CONFIG = new InjectionToken<IAnalyticsConfig>(
  'ANALYTICS_CONFIG',
  {
    providedIn: 'root',
    factory: () => ({
      enabled: environment.mixpanelEnabled,
      token: environment.mixpanelToken,
    }),
  },
);

export const MIXPANEL_CLIENT = new InjectionToken<typeof mixpanel>(
  'MIXPANEL_CLIENT',
  {
    providedIn: 'root',
    factory: () => mixpanel,
  },
);

interface IPendingEvent {
  eventName: string;
  properties?: Dict;
}

@Service()
export class AnalyticsService {
  private readonly _config = inject(ANALYTICS_CONFIG);
  private readonly _mixpanel = inject(MIXPANEL_CLIENT);
  private readonly _pendingEventLimit = 10;
  private _initialized = false;
  private _trackingStarted = false;
  private _pendingEvents: IPendingEvent[] = [];

  init(): void {
    if (!this._config.enabled || this._initialized) return;

    this._mixpanel.init(this._config.token, {
      autocapture: false,
      batch_requests: false,
      debug: false,
      track_pageview: false,
    });
    this._initialized = true;
  }

  track(eventName: string, properties?: Dict): void {
    if (!this._config.enabled) return;

    // Hold events until identify() runs so they attach to the real user,
    // not Mixpanel's anonymous distinct_id. Cap at _pendingEventLimit.
    if (!this._trackingStarted) {
      this._pendingEvents.push({ eventName, properties });
      this._pendingEvents = this._pendingEvents.slice(-this._pendingEventLimit);
      return;
    }

    this._mixpanel.track(eventName, properties);
  }

  identify(userId: string, traits?: Dict): void {
    if (!this._initialized) return;

    this._mixpanel.identify(userId);
    if (traits) this._mixpanel.people.set(traits);

    this._trackingStarted = true;
    this._flushPendingEvents();
  }

  reset(): void {
    this._pendingEvents = [];
    this._trackingStarted = false;

    if (this._initialized) this._mixpanel.reset();
  }

  // Replay queued tracks after identify(). This is not Mixpanel batching;
  // it is "send the events we stored because tracking had not started yet".
  private _flushPendingEvents(): void {
    this._pendingEvents.forEach(({ eventName, properties }) =>
      this._mixpanel.track(eventName, properties),
    );
    this._pendingEvents = [];
  }
}
